// Relative path: src/components/Popup/DoctorPopup.js

import React, { useState, useEffect } from 'react';
import Popup from './Popup';
import ReviewBlock from '../ReviewBlock';
import recUtils from '../../utils/RecUtils';
import websiteUtils from '../../utils/WebsiteUtils';

const DoctorPopup = ({ isOpen, onClose, doctor }) => {
  const [quotesData, setQuotesData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Helper to parse quote strings in format "[reviewId] quote text"
  const parseQuoteString = (quoteStr) => {
    if (!quoteStr || typeof quoteStr !== 'string') return null;
    
    const match = quoteStr.match(/^\[(.*?)\]\s+(.+)$/);
    if (!match || !match[1] || !match[1].trim()) return null;
    
    return {
      reviewId: match[1].trim(),
      quoteText: match[2]
    };
  };

  // Helper to format timestamp
  const formatTimestamp = (timestampValue) => {
    if (!timestampValue) return "";
    
    let date;
    if (timestampValue && typeof timestampValue.toDate === 'function') {
      date = timestampValue.toDate();
    } else {
      date = new Date(timestampValue);
    }
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };
  
  useEffect(() => {
    if (!isOpen) {
      setQuotesData({});
      setLoading(false);
      setError(null);
      return;
    }
    
    if (!doctor) return;
    
    const doctorReviewsPros = Array.isArray(doctor.doctorReviewsPros) ? doctor.doctorReviewsPros : [];
    const doctorReviewsCons = Array.isArray(doctor.doctorReviewsCons) ? doctor.doctorReviewsCons : [];
    
    if (doctorReviewsPros.length === 0 && doctorReviewsCons.length === 0) return;
    
    let isMounted = true;
    
    const fetchQuotesData = async () => {
      if (loading) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const allQuotes = [];
        
        for (const quoteStr of doctorReviewsPros) {
          const parsed = parseQuoteString(quoteStr);
          if (parsed && parsed.reviewId) {
            allQuotes.push({ ...parsed, isPro: true });
          }
        }
        
        for (const quoteStr of doctorReviewsCons) {
          const parsed = parseQuoteString(quoteStr);
          if (parsed && parsed.reviewId) {
            allQuotes.push({ ...parsed, isPro: false });
          }
        }
        
        if (allQuotes.length === 0) {
          if (isMounted) {
            setLoading(false);
          }
          return;
        }
        
        const reviewIds = [...new Set(allQuotes.map(q => q.reviewId))];
        
        if (reviewIds.length > 20) {
          reviewIds.length = 20;
        }
        
        const reviewsData = {};
        
        for (const reviewId of reviewIds) {
          if (!isMounted) break;
          
          try {
            const reviewData = await recUtils.fetchReviewFromId(reviewId);
            
            if (!reviewData) {
              continue;
            }
            
            const prosQuotes = allQuotes
              .filter(q => q.reviewId === reviewId && q.isPro)
              .map(q => q.quoteText);
            
            const consQuotes = allQuotes
              .filter(q => q.reviewId === reviewId && !q.isPro)
              .map(q => q.quoteText);
            
            // Use centralized method for website source info
            const websiteInfo = websiteUtils.getWebsiteSourceInfo(reviewData);
            const reviewerName = reviewData.reviewer_name || "Anonymous";
            
            // Combine quotes with sentiment for ReviewBlock
            const displayedQuotes = [
              ...prosQuotes.map(text => ({ text, sentiment: 'positive' })),
              ...consQuotes.map(text => ({ text, sentiment: 'negative' }))
            ];
            
            reviewsData[reviewId] = {
              reviewId,
              reviewerName,
              satisfactionLevel: reviewData.user_satisfaction || -1,
              externalUrl: websiteInfo.external_url,
              websiteSource: websiteInfo.displayName,
              siteLogo: websiteInfo.siteLogo,
              authorImg: null, // Will use dynamic profile picture assignment
              operationDate: reviewData.timestamp ? formatTimestamp(reviewData.timestamp) : "",
              dynamicLabels: [], // No dynamic labels for doctor reviews
              displayedQuotes,
              isExternal: websiteInfo.isExternal
            };
          } catch (err) {
            console.error(`Error fetching review ${reviewId}:`, err);
          }
        }
        
        if (isMounted) {
          setQuotesData(reviewsData);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error in fetchQuotesData:", err);
        if (isMounted) {
          setError("Failed to load reviews");
          setLoading(false);
        }
      }
    };
    
    fetchQuotesData();
    
    return () => {
      isMounted = false;
    };
  }, [isOpen, doctor]);

  // Cleanup profile picture assignments when component unmounts
  useEffect(() => {
    return () => {
      // Collect all review IDs to release
      Object.keys(quotesData).forEach(reviewId => {
        recUtils.releaseProfilePicture(reviewId);
      });
    };
  }, [quotesData]);
  
  if (!doctor) return <Popup isOpen={isOpen} onClose={onClose} />;
  
  const { 
    doctorName, 
    doctorTitle, 
    doctorImageUrl, 
    doctorCredentials = [],
    doctorReviewsPros = [],
    doctorReviewsCons = []
  } = doctor;
  
  const hasBulletPoints = Array.isArray(doctorCredentials) && doctorCredentials.length > 0;
  const hasReviews = 
    (Array.isArray(doctorReviewsPros) && doctorReviewsPros.length > 0) || 
    (Array.isArray(doctorReviewsCons) && doctorReviewsCons.length > 0);
  
  return (
    <Popup isOpen={isOpen} onClose={onClose}>
      <div className="pb-6">
        {/* Doctor header */}
        <div className="pt-12 px-6 pb-6">
          <div className="flex items-start pr-10">
            {/* Doctor image */}
            <div className="w-32 h-32 bg-gray-200 rounded-lg mr-4 flex-shrink-0 overflow-hidden">
              {doctorImageUrl ? (
                <img 
                  src={doctorImageUrl} 
                  alt={doctorName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center text-gray-500">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
            
            {/* Doctor info */}
            <div className="flex-1">
              <h3 className="font-bold text-gray-800 text-xl">
                {doctorName || 'Unknown Doctor'}
              </h3>
              
              <p className="text-gray-600 text-sm mt-1">
                {doctorTitle || 'Cosmetic Surgeon, M.D.'}
              </p>
              
              {/* Bullet points - only show if credentials exist */}
              {hasBulletPoints && (
                <ul className="mt-3 space-y-2">
                  {doctorCredentials.map((credential, index) => (
                    <li key={index} className="flex items-start">
                      <span className="flex-shrink-0 text-green-500 mr-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                      <span className="text-sm text-gray-700">{credential}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        
        {/* Doctor quotes - only show if we have reviews */}
        {hasReviews && (
          <div className="px-6">
            <h4 className="font-bold text-gray-800 mb-3">What Former Patients Say</h4>
            
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-mm_blue" />
              </div>
            ) : error ? (
              <div className="text-red-500 text-sm py-2">
                Error loading reviews: {error}
              </div>
            ) : Object.keys(quotesData).length === 0 ? (
              <div className="text-gray-500 text-sm py-2">
                No review data available
              </div>
            ) : (
              <div className="space-y-6">
                {Object.values(quotesData).map((reviewBlockData, idx) => (
                  <ReviewBlock
                    key={idx}
                    {...reviewBlockData}
                    className="border-2 p-2"
                    virtualizeRendering={false}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Popup>
  );
};

export default DoctorPopup;